/**
 * Draws the map: biome fills in the terrain layer, everything about ownership
 * in the civ layer.
 *
 * The performance shape here is the whole point, and it is the one documented in
 * `docs/02_TECHNICAL_ARCHITECTURE.md` and `docs/06_RESEARCH_NOTES.md`:
 *
 * - **One texture, many tinted sprites.** A single white hex polygon is baked to
 *   a texture once, then every visible hex is a `Sprite` of it with a tint. All
 *   the terrain sprites share a texture, so they batch into one draw call. The
 *   path this avoids is a `Graphics` per hex, which is CPU-bound on geometry
 *   rebuilds and falls over well before 1600 hexes.
 * - **Pool and reuse.** Sprites are indexed slots that get repositioned and
 *   retinted across culling passes, never created and destroyed. A 40x40 map
 *   panning at 60fps would otherwise churn hundreds of objects a second.
 * - **Cull, and only on pan/zoom.** `updateVisible` is the only thing that walks
 *   the map, and it exits early when neither the visible set nor the zoom
 *   bucket has moved. Nothing here runs per frame.
 *
 * Escalation path, per docs/02 and docs/06: if visible tile count outgrows
 * tinted sprites, `@pixi/tilemap` is the documented next step. Do not adopt it
 * preemptively — measure first.
 *
 * Vector work (borders, meters, highlights) stays as `Graphics` because it is
 * proportional to the *frontier*, not to the map, and it is redrawn only when
 * something it depends on changes.
 */

import { Container, FillGradient, Graphics, Sprite } from "pixi.js";
import type { Renderer, Texture } from "pixi.js";
import {
  BIOMES,
  HEX_DIRECTION_COUNT,
  clamp,
  clamp01,
  hexCorners,
  hexKey,
  hexNeighbor,
  hexToPixel,
  parseBorderSegmentId,
  hexesInRect,
} from "kngdm-shared";
import type {
  Biome,
  BuildingType,
  HexCoord,
  HexState,
  KingdomId,
  KingdomState,
  MapState,
  SpecialHex,
} from "kngdm-shared";
import type { Camera } from "./camera";
import type { RenderLayers } from "./layers";
import {
  BIOME_COLORS,
  BORDER_CASING,
  BORDER_CASING_SCALE,
  FLASH_CAPTURE,
  FLASH_REJECT,
  DECOR_COLORS,
  DECOR_HIGHLIGHT,
  TERRAIN_LIT,
  TERRAIN_SHADE,
  TROOP_CASING,
  BUILDING_GLYPH,
  EVENT_TINT,
  EVENT_TINT_STRENGTH,
  FORTIFY_RING,
  SPECIAL_GLYPH,
  SPECIAL_HALO,
  BUILDING_HALO,
  CAPTURE_FILL,
  CAPTURE_TRACK,
  CLAIM_PREVIEW,
  HOVER_OUTLINE,
  NEUTRAL_OUTLINE,
  SELECTION_OUTLINE,
  UNCLAIMED_TINT,
  blendColors,
} from "./palette";

export interface HexRendererOptions {
  /** Hex circumradius in world pixels, matching `hexToPixel`'s `size`. */
  readonly hexSize: number;
}

/** Circumradius the hex texture is baked at. Oversized so zooming in stays crisp. */
const TEXTURE_HEX_RADIUS = 64;

/**
 * Terrain sprites are drawn a hair oversized so antialiased edges overlap
 * instead of leaving a seam of background between neighbours. Ownership fills
 * are not, because their edges must line up with the drawn borders.
 */
const TERRAIN_BLEED = 1.02;

/** Fraction of the texture radius given to the baked rim. Reads as a hex grid. */
const RIM_FRACTION = 0.045;

/** How much unowned terrain is pulled toward neutral so owned land reads forward. */
const UNCLAIMED_DESATURATION = 0.22;

/**
 * Ownership fill opacity ramps with zoom. Close in, borders carry ownership and
 * the fill can stay light enough to read the biome through it; at continent
 * zoom a border is barely a pixel, so territory has to read as coloured mass.
 */
const OWNER_ALPHA_NEAR = 0.3;
const OWNER_ALPHA_FAR = 0.62;
const HEX_SCREEN_PX_NEAR = 26;
const HEX_SCREEN_PX_FAR = 6;

/** Target on-screen thickness for civ-layer strokes, in screen pixels. */
const BORDER_SCREEN_PX = 3;

/**
 * How long a one-off feedback flash lasts, in milliseconds.
 *
 * Short. A flash is an acknowledgement, not an animation to sit through — long
 * enough to be certain you saw it, short enough that ten of them during a busy
 * frontline do not turn the map into a strobe.
 */
const FLASH_MS = 520;

/**
 * Fraction of the remaining gap a displayed capture value closes per second.
 *
 * The simulation steps capture ten times a second, so drawing the raw value
 * makes the meter climb in visible stair-steps. Easing toward it turns the same
 * data into a sweep.
 */
const CAPTURE_EASE_PER_SEC = 12;

/** Seconds per full pulse cycle on a contested hex's rim. */
const CONTEST_PULSE_HZ = 1.6;

/** Below this many troops a hex draws no marker, so quiet interiors stay clean. */
const TROOP_VISIBLE_MIN = 0.35;

/** Troop count that fills the marker to its maximum radius. */
const TROOP_FULL_SCALE = 26;
const HIGHLIGHT_SCREEN_PX = 2.5;

/** Strokes are clamped to a fraction of a hex so they never swallow the map. */
const MIN_STROKE_HEX_FRACTION = 0.05;
const MAX_STROKE_HEX_FRACTION = 0.34;

/** Below this the capture meter is noise rather than information. */
const CAPTURE_VISIBLE_THRESHOLD = 0.01;

/** Zoom must move this much before vector layers are redrawn. */
const ZOOM_REDRAW_STEP = 0.05;

/**
 * Corner pair bounding the edge shared with neighbour `direction`, indexed into
 * `hexCorners`' output. Derived from the pointy-top corner angles (-30 + 60i)
 * against `HEX_DIRECTIONS`; see the table in `hexGrid.ts`.
 */
function edgeCornerIndex(direction: number): number {
  return (HEX_DIRECTION_COUNT - direction) % HEX_DIRECTION_COUNT;
}

interface Offset {
  readonly x: number;
  readonly y: number;
}

export class HexRenderer {
  private readonly layers: RenderLayers;
  private readonly renderer: Renderer;

  private hexSizeValue: number;

  private readonly hexTileTexture: Texture;
  private readonly hexFillTexture: Texture;
  private readonly buildingTextures: Map<BuildingType, Texture>;
  private readonly specialTextures: Map<SpecialHex, Texture>;

  /** Sub-containers of `civ`, so z-order inside the layer is explicit. */
  private readonly ownerFills = new Container();
  private readonly previewGfx = new Graphics();
  private readonly captureGfx = new Graphics();
  private readonly borderGfx = new Graphics();
  /** Troop markers, above borders so a garrison is never hidden by its own edge. */
  private readonly troopGfx = new Graphics();
  /** Surge chevrons, above troops so a push is never hidden by its own garrison. */
  private readonly surgeGfx = new Graphics();
  /** Segment id -> the kingdom pushing it. */
  private readonly surgesBySegment = new Map<string, KingdomId>();
  private readonly buildingHalos = new Container();
  private readonly buildingGlyphs = new Container();
  private readonly hoverGfx = new Graphics();
  private readonly selectionGfx = new Graphics();

  /**
   * Special-hex markers live in the *terrain* layer, not `civ`.
   *
   * docs/05: the world layer carries what the world is, the civ layer carries who
   * owns it. A geyser is a fact about the map that exists before anyone claims it
   * and stays when the hex changes hands, so ownership fills and borders are
   * drawn over the top of it rather than the other way round. It also means a
   * player can see what is worth expanding toward on unclaimed ground.
   */
  /** Decorations live in the terrain layer, above fills and below ownership. */
  private readonly decorLayer = new Container();
  private readonly decorSprites: Sprite[] = [];
  private readonly decorTextures = new Map<Biome, Texture | null>();

  private readonly specialHalos = new Container();
  private readonly specialGlyphs = new Container();

  private readonly terrainSprites: Sprite[] = [];
  private readonly ownerSprites: Sprite[] = [];
  private readonly haloSprites: Sprite[] = [];
  private readonly glyphSprites: Sprite[] = [];
  private readonly specialHaloSprites: Sprite[] = [];
  private readonly specialGlyphSprites: Sprite[] = [];

  private readonly hexByKey = new Map<string, HexState>();
  private readonly kingdomColors = new Map<KingdomId, number>();

  private visibleCoords: HexCoord[] = [];
  private readonly indexByKey = new Map<string, number>();
  private visibleDirty = true;

  private cornerOffsets: Offset[] = [];

  private zoomValue = 1;
  private lastRedrawZoom = 0;

  /**
   * Wall-clock the renderer has been running, in milliseconds.
   *
   * Everything time-based reads from this rather than `performance.now()` so a
   * paused or throttled tab does not accumulate phase it never drew — the
   * pulses stay in step with the frames the player actually sees.
   */
  private timeMs = 0;

  /** Displayed capture per hex, easing toward the authoritative value. */
  private readonly captureEased = new Map<string, number>();

  /** Active feedback flashes: hex key -> remaining ms and colour. */
  private readonly flashes = new Map<string, { remainingMs: number; color: number }>();

  private wasAnimating = false;

  private hover: HexCoord | null = null;
  private selected: HexCoord | null = null;
  private claimPreview: readonly HexCoord[] = [];

  constructor(layers: RenderLayers, renderer: Renderer, options: HexRendererOptions) {
    if (!(options.hexSize > 0)) {
      throw new Error(`HexRenderer hexSize must be positive, got ${options.hexSize}`);
    }

    this.layers = layers;
    this.renderer = renderer;
    this.hexSizeValue = options.hexSize;

    this.hexTileTexture = this.bakeHexTexture(true);
    this.hexFillTexture = this.bakeHexTexture(false);
    this.buildingTextures = new Map([["sawmill", this.bakeSawmillTexture()]]);
    for (const biome of BIOMES) this.decorTextures.set(biome, this.bakeDecorTexture(biome));

    this.specialTextures = new Map([
      ["ruins", this.bakeRuinsTexture()],
      ["geyser", this.bakeGeyserTexture()],
      ["crystalVein", this.bakeCrystalTexture()],
    ]);

    this.layers.terrain.addChild(this.decorLayer);
    this.layers.terrain.addChild(this.specialHalos);
    this.layers.terrain.addChild(this.specialGlyphs);

    // Inside the civ layer: ownership mass at the bottom, affordances on top,
    // so a hover highlight is never hidden under a border.
    this.layers.civ.addChild(this.ownerFills);
    this.layers.civ.addChild(this.previewGfx);
    this.layers.civ.addChild(this.captureGfx);
    this.layers.civ.addChild(this.borderGfx);
    this.layers.civ.addChild(this.troopGfx);
    this.layers.civ.addChild(this.surgeGfx);
    this.layers.civ.addChild(this.buildingHalos);
    this.layers.civ.addChild(this.buildingGlyphs);
    this.layers.civ.addChild(this.hoverGfx);
    this.layers.civ.addChild(this.selectionGfx);

    this.recomputeCornerOffsets();
  }

  get hexSize(): number {
    return this.hexSizeValue;
  }

  /** Changing hex size rescales every pooled sprite and rebuilds vector work. */
  set hexSize(size: number) {
    if (!(size > 0)) throw new Error(`hexSize must be positive, got ${size}`);
    if (size === this.hexSizeValue) return;
    this.hexSizeValue = size;
    this.recomputeCornerOffsets();
    this.visibleDirty = true;
    this.refresh();
  }

  /** Replaces the whole map. The next `updateVisible` picks the new hexes up. */
  setMap(map: MapState): void {
    this.hexByKey.clear();
    for (const hexState of map.hexes) {
      this.hexByKey.set(hexKey(hexState.coord), hexState);
    }
    this.visibleDirty = true;
    this.refresh();
  }

  /**
   * Kingdom colours arrive on the wire in `KingdomState.color`; the client never
   * decides them. Cached by id because border drawing needs a colour per owned
   * hex and a linear scan per hex would be the slow part of a redraw.
   */
  setKingdoms(kingdoms: readonly KingdomState[]): void {
    this.kingdomColors.clear();
    for (const kingdom of kingdoms) {
      this.kingdomColors.set(kingdom.id, kingdom.color);
    }
    this.refresh();
  }

  /**
   * Applies a per-tick state delta. Cheap for the common case — a few hexes
   * whose capture meter moved — and only escalates to a border redraw when
   * ownership actually changed.
   */
  applyDelta(changed: readonly HexState[]): void {
    let ownershipChanged = false;
    let buildingChanged = false;
    let captureChanged = false;

    for (const hexState of changed) {
      const key = hexKey(hexState.coord);
      const previous = this.hexByKey.get(key);
      this.hexByKey.set(key, hexState);

      if (previous === undefined || previous.owner !== hexState.owner) {
        ownershipChanged = true;
        // The payoff moment. Only for a hex that actually changed hands mid-session
        // — `previous === undefined` is the initial load, where flashing the whole
        // map would be a fireworks display rather than feedback.
        if (previous !== undefined) this.flash(hexState.coord, "capture");
      }
      if (previous === undefined || previous.building !== hexState.building) buildingChanged = true;
      if (previous === undefined || previous.fortified !== hexState.fortified) captureChanged = true;
      if (previous === undefined || previous.capture !== hexState.capture) captureChanged = true;

      const index = this.indexByKey.get(key);
      if (index !== undefined) this.applyHexToSprites(index, hexState);
    }

    // A hex flipping owner changes the edges of its neighbours too, so borders
    // are redrawn wholesale over the visible set rather than patched.
    if (ownershipChanged) this.redrawBorders();
    if (captureChanged || ownershipChanged) this.redrawCapture();
    if (buildingChanged || ownershipChanged) this.redrawBuildings();
    this.redrawTroops();
  }

  /**
   * Advance every time-based visual. Call once per rendered frame.
   *
   * Before this existed the renderer had no concept of time at all: every change
   * — including a hex changing owner, the most important event in the game —
   * happened between one frame and the next with no transition and no
   * acknowledgement. Redraw-on-change is correct for terrain and borders, which
   * only move when the world does; it is wrong for anything the player should
   * *feel*.
   *
   * Only the two dynamic layers are rebuilt here. Terrain, borders, buildings and
   * specials stay on the change-driven path, so the per-frame cost is bounded by
   * how much is actually animating rather than by map size.
   */
  update(dtMs: number): void {
    if (!(dtMs > 0)) return;
    this.timeMs += dtMs;

    const dt = dtMs / 1000;
    let animating = false;

    // Ease displayed capture toward the authoritative value. Exponential rather
    // than linear so it arrives quickly and settles softly.
    const blend = 1 - Math.exp(-CAPTURE_EASE_PER_SEC * dt);
    for (const coord of this.visibleCoords) {
      const key = hexKey(coord);
      const target = clamp01(this.hexByKey.get(key)?.capture ?? 0);
      const shown = this.captureEased.get(key) ?? target;
      const next = shown + (target - shown) * blend;

      if (Math.abs(target - next) < 0.001) {
        if (target <= 0) this.captureEased.delete(key);
        else this.captureEased.set(key, target);
      } else {
        this.captureEased.set(key, next);
        animating = true;
      }
      if (target > 0) animating = true;
    }

    if (this.surgesBySegment.size > 0) animating = true;

    for (const [key, flash] of this.flashes) {
      flash.remainingMs -= dtMs;
      if (flash.remainingMs <= 0) this.flashes.delete(key);
      else animating = true;
    }

    // Redraw the dynamic layers only while something is actually moving. A quiet
    // map costs nothing, which is what makes it affordable to run this every frame.
    if (animating || this.wasAnimating) {
      this.redrawCapture();
      this.redrawTroops();
      this.redrawSurges();
    }
    this.wasAnimating = animating;
  }

  /**
   * Mark a hex for a one-off flash.
   *
   * `capture` is the payoff when territory changes hands; `reject` is the answer
   * to a click the rules refused, which until now produced no response at all.
   */
  flash(coord: HexCoord, kind: "capture" | "reject"): void {
    this.flashes.set(hexKey(coord), {
      remainingMs: FLASH_MS,
      color: kind === "capture" ? FLASH_CAPTURE : FLASH_REJECT,
    });
  }

  /**
   * Reculls against the camera. Safe to call every frame — it exits early unless
   * the visible hex set or the zoom bucket moved, so the real cost is paid on
   * pan and zoom only, per docs/02.
   */
  updateVisible(camera: Camera): void {
    const rect = camera.visibleWorldRect();
    const candidates = hexesInRect(rect.topLeft, rect.bottomRight, this.hexSizeValue);

    const coords: HexCoord[] = [];
    for (const coord of candidates) {
      if (this.hexByKey.has(hexKey(coord))) coords.push(coord);
    }

    const zoom = camera.zoom;
    const zoomMoved =
      this.lastRedrawZoom <= 0 ||
      zoom > this.lastRedrawZoom * (1 + ZOOM_REDRAW_STEP) ||
      zoom < this.lastRedrawZoom * (1 - ZOOM_REDRAW_STEP);

    if (!zoomMoved && !this.visibleDirty && sameCoords(coords, this.visibleCoords)) return;

    this.zoomValue = zoom;
    if (zoomMoved) this.lastRedrawZoom = zoom;

    this.syncVisible(coords);
    this.redrawBorders();
    this.redrawCapture();
    this.redrawBuildings();
    // Specials never change after generation, so this rides the cull rather than
    // the delta: it only has to keep up with which hexes are on screen.
    this.redrawSpecials();
    this.redrawDecor();
    this.redrawHighlights();
  }

  setHover(coord: HexCoord | null): void {
    if (sameCoord(coord, this.hover)) return;
    this.hover = coord;
    this.drawHover();
  }

  setSelected(coord: HexCoord | null): void {
    if (sameCoord(coord, this.selected)) return;
    this.selected = coord;
    this.drawSelection();
  }

  /** Hexes the player could claim right now; the client greys nothing itself. */
  setClaimPreview(coords: readonly HexCoord[]): void {
    this.claimPreview = coords;
    this.drawClaimPreview();
  }

  destroy(): void {
    for (const sprite of this.terrainSprites) sprite.destroy();
    for (const sprite of this.ownerSprites) sprite.destroy();
    for (const sprite of this.haloSprites) sprite.destroy();
    for (const sprite of this.glyphSprites) sprite.destroy();
    this.terrainSprites.length = 0;
    this.ownerSprites.length = 0;
    this.haloSprites.length = 0;
    this.glyphSprites.length = 0;

    this.ownerFills.destroy();
    this.buildingHalos.destroy();
    this.buildingGlyphs.destroy();
    this.decorLayer.destroy();
    for (const texture of this.decorTextures.values()) texture?.destroy(true);
    this.decorTextures.clear();
    this.specialHalos.destroy();
    this.specialGlyphs.destroy();
    this.previewGfx.destroy();
    this.captureGfx.destroy();
    this.borderGfx.destroy();
    this.troopGfx.destroy();
    this.surgeGfx.destroy();
    this.hoverGfx.destroy();
    this.selectionGfx.destroy();

    // `true` frees the underlying texture source, not just the view — these are
    // render textures this instance created and nothing else references.
    this.hexTileTexture.destroy(true);
    this.hexFillTexture.destroy(true);
    for (const texture of this.buildingTextures.values()) texture.destroy(true);
    this.buildingTextures.clear();
    for (const texture of this.specialTextures.values()) texture.destroy(true);
    this.specialTextures.clear();

    this.hexByKey.clear();
    this.kingdomColors.clear();
    this.indexByKey.clear();
    this.visibleCoords = [];
  }

  // -------------------------------------------------------------------------
  // Texture baking
  // -------------------------------------------------------------------------

  /**
   * One white hex, baked once. `withRim` bakes a slightly darker inner edge:
   * because the sprite is tinted, that rim becomes a darker shade of whatever
   * biome colour it carries, which gives the terrain a visible hex grid for
   * zero extra draw calls.
   */
  /**
   * The hex texture, with the light direction baked in.
   *
   * Sprite tint *multiplies* against the texture, so a texture that fades from
   * white at the top to a grey at the bottom gives every hex on the map a lit
   * top edge and a shaded lower edge — for free, with no per-hex work and no
   * extra draw calls. That matters because the whole terrain layer is one
   * batched draw of one shared texture, and per-hex shading would destroy it.
   *
   * `TERRAIN_LIT` is 1.0 for a reason worth stating: tint cannot brighten past
   * the texture, so white *is* the fully-lit value and the gradient can only
   * work downward from it.
   */
  private bakeHexTexture(withRim: boolean): Texture {
    const graphics = new Graphics();
    const points = hexCorners({ q: 0, r: 0 }, TEXTURE_HEX_RADIUS).flatMap((p) => [p.x, p.y]);

    const shade = new FillGradient(0, -TEXTURE_HEX_RADIUS, 0, TEXTURE_HEX_RADIUS);
    shade.addColorStop(0, greyOf(TERRAIN_LIT));
    shade.addColorStop(1, greyOf(TERRAIN_SHADE));

    graphics.poly(points).fill(shade);
    if (withRim) {
      graphics
        .poly(points)
        .stroke({ width: TEXTURE_HEX_RADIUS * RIM_FRACTION, color: NEUTRAL_OUTLINE, alignment: 1 });
    }

    const texture = this.renderer.generateTexture({ target: graphics, antialias: true });
    graphics.destroy();
    return texture;
  }

  /**
   * A saw blade: a twelve-toothed star. Deliberately a silhouette rather than a
   * drawing — it is tinted flat and drawn over a dark halo, so what has to
   * survive at small sizes is the outline, not detail.
   */
  /** Broken arch: two uprights and a gapped lintel. */
  private bakeRuinsTexture(): Texture {
    const r = TEXTURE_HEX_RADIUS;
    const g = new Graphics();
    g.rect(-r * 0.62, -r * 0.2, r * 0.3, r * 1.0).fill(0xffffff);
    g.rect(r * 0.32, -r * 0.2, r * 0.3, r * 1.0).fill(0xffffff);
    g.rect(-r * 0.62, -r * 0.62, r * 0.72, r * 0.28).fill(0xffffff);
    return this.bakeGlyph(g);
  }

  /** Geyser: a narrow plume widening upward, over a basin. */
  private bakeGeyserTexture(): Texture {
    const r = TEXTURE_HEX_RADIUS;
    const g = new Graphics();
    g.poly([-r * 0.18, r * 0.5, r * 0.18, r * 0.5, r * 0.5, -r * 0.72, -r * 0.5, -r * 0.72])
      .fill(0xffffff);
    g.ellipse(0, r * 0.62, r * 0.62, r * 0.2).fill(0xffffff);
    return this.bakeGlyph(g);
  }

  /** Crystal vein: three upright shards of differing height. */
  private bakeCrystalTexture(): Texture {
    const r = TEXTURE_HEX_RADIUS;
    const g = new Graphics();
    g.poly([-r * 0.6, r * 0.6, -r * 0.24, r * 0.6, -r * 0.42, -r * 0.34]).fill(0xffffff);
    g.poly([-r * 0.2, r * 0.66, r * 0.2, r * 0.66, 0, -r * 0.76]).fill(0xffffff);
    g.poly([r * 0.24, r * 0.6, r * 0.6, r * 0.6, r * 0.42, -r * 0.28]).fill(0xffffff);
    return this.bakeGlyph(g);
  }

  private bakeGlyph(graphics: Graphics): Texture {
    const texture = this.renderer.generateTexture({ target: graphics, antialias: true });
    graphics.destroy();
    return texture;
  }

  private bakeSawmillTexture(): Texture {
    const teeth = 12;
    const outer = TEXTURE_HEX_RADIUS;
    const inner = TEXTURE_HEX_RADIUS * 0.74;
    const points: number[] = [];
    for (let i = 0; i < teeth * 2; i++) {
      const radius = i % 2 === 0 ? outer : inner;
      const angle = (Math.PI * i) / teeth;
      points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }

    const graphics = new Graphics();
    graphics.poly(points).fill(0xffffff);
    const texture = this.renderer.generateTexture({ target: graphics, antialias: true });
    graphics.destroy();
    return texture;
  }

  // -------------------------------------------------------------------------
  // Sprite pooling
  // -------------------------------------------------------------------------

  private recomputeCornerOffsets(): void {
    // Corner offsets from a hex centre are translation-invariant, so they are
    // computed once from the shared helper and reused for every hex instead of
    // allocating six points per hex per redraw.
    this.cornerOffsets = hexCorners({ q: 0, r: 0 }, this.hexSizeValue).map((p) => ({
      x: p.x,
      y: p.y,
    }));
  }

  /** Re-syncs sprites and vector layers against the current visible set. */
  private refresh(): void {
    const coords: HexCoord[] = [];
    for (const coord of this.visibleCoords) {
      if (this.hexByKey.has(hexKey(coord))) coords.push(coord);
    }
    this.syncVisible(coords);
    this.redrawBorders();
    this.redrawCapture();
    this.redrawBuildings();
    // Specials never change after generation, so this rides the cull rather than
    // the delta: it only has to keep up with which hexes are on screen.
    this.redrawSpecials();
    this.redrawHighlights();
  }

  private syncVisible(coords: HexCoord[]): void {
    this.visibleCoords = coords;
    this.visibleDirty = false;
    this.indexByKey.clear();

    for (let index = 0; index < coords.length; index++) {
      const coord = coords[index];
      if (coord === undefined) continue;
      const key = hexKey(coord);
      this.indexByKey.set(key, index);
      const state = this.hexByKey.get(key);
      if (state === undefined) continue;
      this.applyHexToSprites(index, state);
    }

    // Surplus slots are hidden, never destroyed: the viewport is bounded, so the
    // pool converges on the largest visible count and stops allocating.
    for (let index = coords.length; index < this.terrainSprites.length; index++) {
      const terrain = this.terrainSprites[index];
      const owner = this.ownerSprites[index];
      if (terrain !== undefined) terrain.visible = false;
      if (owner !== undefined) owner.visible = false;
    }
  }

  private applyHexToSprites(index: number, state: HexState): void {
    const centre = hexToPixel(state.coord, this.hexSizeValue);
    const terrainScale = (this.hexSizeValue / TEXTURE_HEX_RADIUS) * TERRAIN_BLEED;
    const fillScale = this.hexSizeValue / TEXTURE_HEX_RADIUS;

    const terrain = this.terrainSpriteAt(index);
    terrain.visible = true;
    terrain.position.set(centre.x, centre.y);
    terrain.scale.set(terrainScale);
    let biomeColor = BIOME_COLORS[state.biome];
    // The event tint goes on the *terrain* sprite, not into civLayer, so the
    // disaster reads as something happening to the ground while the ownership
    // border on top stays exactly as legible — the hard rule in docs/05.
    if (state.eventAffected) {
      biomeColor = blendColors(biomeColor, EVENT_TINT, EVENT_TINT_STRENGTH);
    }
    terrain.tint =
      state.owner === null
        ? blendColors(biomeColor, UNCLAIMED_TINT, UNCLAIMED_DESATURATION)
        : biomeColor;

    const ownerSprite = this.ownerSpriteAt(index);
    if (state.owner === null) {
      ownerSprite.visible = false;
      return;
    }
    ownerSprite.visible = true;
    ownerSprite.position.set(centre.x, centre.y);
    ownerSprite.scale.set(fillScale);
    ownerSprite.tint = this.colorFor(state.owner);
    ownerSprite.alpha = this.ownerFillAlpha();
  }

  private terrainSpriteAt(index: number): Sprite {
    const existing = this.terrainSprites[index];
    if (existing !== undefined) return existing;
    const sprite = new Sprite(this.hexTileTexture);
    sprite.anchor.set(0.5);
    this.terrainSprites[index] = sprite;
    this.layers.terrain.addChild(sprite);
    return sprite;
  }

  private ownerSpriteAt(index: number): Sprite {
    const existing = this.ownerSprites[index];
    if (existing !== undefined) return existing;
    const sprite = new Sprite(this.hexFillTexture);
    sprite.anchor.set(0.5);
    this.ownerSprites[index] = sprite;
    this.ownerFills.addChild(sprite);
    return sprite;
  }

  private specialSpriteAt(index: number, halo: boolean, texture: Texture): Sprite {
    const pool = halo ? this.specialHaloSprites : this.specialGlyphSprites;
    let sprite = pool[index];
    if (sprite === undefined) {
      sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      pool[index] = sprite;
      (halo ? this.specialHalos : this.specialGlyphs).addChild(sprite);
    }
    return sprite;
  }

  /**
   * Special-hex markers for every visible special, owned or not.
   *
   * Unlike buildings these are drawn regardless of ownership: an unclaimed geyser
   * is precisely the thing a player needs to see in order to decide where to
   * expand, and hiding it until someone owns it would make the whole Aether
   * economy invisible at the moment it matters most.
   */

  // -------------------------------------------------------------------------
  // Terrain decorations
  //
  // Trees, rocks and dunes drawn in code rather than authored as art. The
  // flat-vector direction in docs/05 rules out tile files, and generated shapes
  // mean a new biome is a function rather than an asset pipeline.
  //
  // One texture is baked per biome and reused as tinted sprites, exactly like
  // the terrain hexes, so the whole decoration layer batches into a single draw
  // no matter how many are on screen.
  // -------------------------------------------------------------------------

  /** Bake one decoration texture per biome, at the shared texture radius. */
  private bakeDecorTexture(biome: Biome): Texture | null {
    const g = new Graphics();
    const r = TEXTURE_HEX_RADIUS;
    const body = DECOR_COLORS[biome];
    const lit = DECOR_HIGHLIGHT[biome];

    switch (biome) {
      case "forest": {
        // A conifer: trunk, then three stacked tiers, lit on the upper left to
        // match the light direction baked into the terrain texture.
        g.rect(-r * 0.06, r * 0.28, r * 0.12, r * 0.34).fill(0x3a2c1e);
        for (let i = 0; i < 3; i++) {
          const y = r * (0.34 - i * 0.28);
          const w = r * (0.46 - i * 0.1);
          g.poly([0, y - r * 0.42, w, y, -w, y]).fill(body);
          g.poly([0, y - r * 0.42, w * 0.34, y - r * 0.06, -w * 0.1, y - r * 0.06]).fill(lit);
        }
        break;
      }
      case "mountains": {
        // A peak with a lit western face and a snow cap.
        g.poly([0, -r * 0.72, r * 0.66, r * 0.5, -r * 0.66, r * 0.5]).fill(body);
        g.poly([0, -r * 0.72, 0, r * 0.5, -r * 0.66, r * 0.5]).fill(lit);
        g.poly([0, -r * 0.72, r * 0.2, -r * 0.3, -r * 0.2, -r * 0.3]).fill(0xd8dee4);
        break;
      }
      case "hills": {
        // Two rounded mounds.
        g.ellipse(-r * 0.16, r * 0.16, r * 0.44, r * 0.32).fill(body);
        g.ellipse(r * 0.22, r * 0.26, r * 0.32, r * 0.22).fill(body);
        g.ellipse(-r * 0.24, r * 0.06, r * 0.26, r * 0.16).fill(lit);
        break;
      }
      case "desert": {
        // A dune crest: a long shallow curve with a lit windward face.
        g.ellipse(0, r * 0.2, r * 0.62, r * 0.22).fill(body);
        g.ellipse(-r * 0.14, r * 0.12, r * 0.4, r * 0.13).fill(lit);
        break;
      }
      case "tundra": {
        // Ice shards.
        g.poly([-r * 0.3, r * 0.3, -r * 0.12, -r * 0.34, 0, r * 0.3]).fill(body);
        g.poly([r * 0.04, r * 0.3, r * 0.24, -r * 0.14, r * 0.4, r * 0.3]).fill(lit);
        break;
      }
      case "plains": {
        // Grass tufts, the lightest touch of the set: plains are where most
        // building and fighting happens, and clutter there costs legibility.
        for (const x of [-r * 0.24, 0, r * 0.24]) {
          g.poly([x, r * 0.28, x - r * 0.07, r * 0.02, x + r * 0.02, r * 0.28]).fill(body);
        }
        g.poly([-r * 0.02, r * 0.28, -r * 0.09, -r * 0.06, r * 0.04, r * 0.28]).fill(lit);
        break;
      }
      /* c8 ignore next 2 */
      default:
        g.destroy();
        return null;
    }

    const texture = this.renderer.generateTexture({ target: g, antialias: true });
    g.destroy();
    return texture;
  }

  /**
   * Scatter decorations over the visible hexes.
   *
   * Placement is a pure function of the hex coordinate, so a hex looks identical
   * every time it is culled back in. A tree that jumps to a new spot when you pan
   * away and back would be worse than no tree at all.
   *
   * Skipped entirely when hexes are small on screen: at continent zoom they turn
   * into noise that competes with the ownership fills for attention.
   */
  private redrawDecor(): void {
    const size = this.hexSizeValue;
    const hexScreenPx = size * this.zoomValue;
    const fade = clamp01((hexScreenPx - 14) / 16);

    let index = 0;
    if (fade > 0) {
      for (const coord of this.visibleCoords) {
        const state = this.hexByKey.get(hexKey(coord));
        if (state === undefined) continue;
        const texture = this.decorTextures.get(state.biome);
        if (texture === undefined || texture === null) continue;
        // A building or a special hex owns its tile; decoration would sit under
        // the glyph and read as clutter.
        if (state.building !== null || state.special !== null) continue;

        const centre = hexToPixel(coord, size);
        const seed = hashCoord(coord);
        const count = state.biome === "forest" ? 3 : 2;

        for (let i = 0; i < count; i++) {
          const a = rand(seed + i * 977);
          const b = rand(seed + i * 331 + 7);
          const c = rand(seed + i * 613 + 19);

          const sprite = this.decorSpriteAt(index);
          sprite.texture = texture;
          sprite.visible = true;
          sprite.alpha = (0.72 + c * 0.28) * fade;

          // Held inside a third of the circumradius so nothing crosses a hex
          // edge and breaks the tiling.
          const angle = a * Math.PI * 2;
          const radius = size * 0.34 * Math.sqrt(b);
          const scale = (size / TEXTURE_HEX_RADIUS) * (0.5 + c * 0.22);

          sprite.position.set(
            centre.x + Math.cos(angle) * radius,
            centre.y + Math.sin(angle) * radius,
          );
          sprite.scale.set(scale);
          index++;
        }
      }
    }

    for (let i = index; i < this.decorSprites.length; i++) {
      const sprite = this.decorSprites[i];
      if (sprite !== undefined) sprite.visible = false;
    }
  }

  private decorSpriteAt(index: number): Sprite {
    let sprite = this.decorSprites[index];
    if (sprite === undefined) {
      sprite = new Sprite();
      sprite.anchor.set(0.5, 0.7);
      this.decorSprites[index] = sprite;
      this.decorLayer.addChild(sprite);
    }
    return sprite;
  }


  /**
   * Draw every active surge as chevrons marching across the pushed edge.
   *
   * This is the one core combat verb in docs/01, and until now it was drawn
   * nowhere at all: a player dragged along a frontier, the server dutifully
   * redistributed troops toward those segments, and the screen said nothing.
   * The whole pressure system was invisible, which is most of why expansion felt
   * like the only thing in the game.
   *
   * Chevrons rather than a static arrow because direction alone is not the
   * interesting part — *commitment* is. They drift toward the target on the
   * shared render clock, so a heavily surged edge visibly streams.
   */
  private redrawSurges(): void {
    this.surgeGfx.clear();
    if (this.surgesBySegment.size === 0) return;

    const size = this.hexSizeValue;
    const width = this.strokeWidth(BORDER_SCREEN_PX * 0.9);
    // One phase for every surge on screen, so a frontline reads as a single
    // coordinated push rather than a row of independent animations.
    const phase = (this.timeMs / 900) % 1;

    for (const [segmentId, kingdomId] of this.surgesBySegment) {
      let parsed;
      try {
        parsed = parseBorderSegmentId(segmentId);
      } catch {
        // A stale id from a segment that stopped being a border. The simulation
        // prunes these itself; skipping is cheaper than validating up front.
        continue;
      }

      const from = this.hexByKey.get(hexKey(parsed.hex));
      if (from === undefined) continue;

      const centre = hexToPixel(parsed.hex, size);
      const target = hexToPixel(hexNeighbor(parsed.hex, parsed.direction), size);

      const dx = target.x - centre.x;
      const dy = target.y - centre.y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      // Perpendicular, for the chevron's arms.
      const px = -uy;
      const py = ux;

      const color = this.colorFor(kingdomId);
      const arm = size * 0.2;

      // Three chevrons spread along the push, cycling outward from the owned hex
      // toward the hex being pushed into.
      for (let i = 0; i < 3; i++) {
        const t = ((i / 3 + phase) % 1);
        // Fade in at the start of the run and out at the end so chevrons appear
        // to flow rather than blink into existence at a fixed point.
        const alpha = Math.sin(t * Math.PI) * 0.85;
        if (alpha <= 0.02) continue;

        const along = 0.18 + t * 0.72;
        const tipX = centre.x + ux * len * along;
        const tipY = centre.y + uy * len * along;
        const backX = tipX - ux * arm;
        const backY = tipY - uy * arm;

        this.surgeGfx
          .moveTo(backX + px * arm * 0.75, backY + py * arm * 0.75)
          .lineTo(tipX, tipY)
          .lineTo(backX - px * arm * 0.75, backY - py * arm * 0.75)
          .stroke({ width, color, alpha, cap: "round", join: "round" });
      }
    }
  }

  /**
   * Tell the renderer which segments are being surged, and by whom.
   *
   * Derived from `KingdomState.surges` rather than tracked client-side, so what
   * is drawn is what the server actually believes — a locally-guessed surge that
   * the server had already pruned would be a lie the player acts on.
   */
  setSurges(kingdoms: readonly KingdomState[]): void {
    this.surgesBySegment.clear();
    for (const kingdom of kingdoms) {
      for (const segmentId of kingdom.surges) {
        this.surgesBySegment.set(segmentId, kingdom.id);
      }
    }
  }

  private redrawSpecials(): void {
    let index = 0;
    const glyphRadius = this.hexSizeValue * 0.46;

    for (const coord of this.visibleCoords) {
      const state = this.hexByKey.get(hexKey(coord));
      if (state === undefined || state.special === null) continue;
      const texture = this.specialTextures.get(state.special);
      if (texture === undefined) continue;

      const centre = hexToPixel(coord, this.hexSizeValue);

      const halo = this.specialSpriteAt(index, true, texture);
      halo.texture = texture;
      halo.visible = true;
      halo.tint = SPECIAL_HALO;
      halo.position.set(centre.x, centre.y);
      halo.scale.set((glyphRadius * 1.34) / TEXTURE_HEX_RADIUS);
      halo.alpha = 0.8;

      const glyph = this.specialSpriteAt(index, false, texture);
      glyph.texture = texture;
      glyph.visible = true;
      glyph.tint = SPECIAL_GLYPH[state.special];
      glyph.position.set(centre.x, centre.y);
      glyph.scale.set(glyphRadius / TEXTURE_HEX_RADIUS);
      glyph.alpha = 1;

      index++;
    }

    for (let i = index; i < this.specialGlyphSprites.length; i++) {
      const halo = this.specialHaloSprites[i];
      const glyph = this.specialGlyphSprites[i];
      if (halo !== undefined) halo.visible = false;
      if (glyph !== undefined) glyph.visible = false;
    }
  }

  private buildingSpriteAt(index: number, halo: boolean, texture: Texture): Sprite {
    const pool = halo ? this.haloSprites : this.glyphSprites;
    const existing = pool[index];
    if (existing !== undefined) return existing;
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    // Tint is set per draw rather than here: a building now carries its owner's
    // colour, and this pool is reused across hexes belonging to different
    // kingdoms as the visible set moves.
    pool[index] = sprite;
    (halo ? this.buildingHalos : this.buildingGlyphs).addChild(sprite);
    return sprite;
  }

  /**
   * A kingdom whose colour hasn't arrived yet renders neutral rather than
   * throwing — a delta can name an owner one message before its `KingdomState`.
   */
  private colorFor(owner: KingdomId): number {
    return this.kingdomColors.get(owner) ?? UNCLAIMED_TINT;
  }

  private ownerFillAlpha(): number {
    const hexScreenPx = this.hexSizeValue * this.zoomValue;
    const t = clamp01(
      (HEX_SCREEN_PX_NEAR - hexScreenPx) / (HEX_SCREEN_PX_NEAR - HEX_SCREEN_PX_FAR),
    );
    return OWNER_ALPHA_NEAR + (OWNER_ALPHA_FAR - OWNER_ALPHA_NEAR) * t;
  }

  /**
   * World-space stroke width that lands on a target *screen* thickness. Borders
   * are the civ layer's whole job; a border that thins to nothing at continent
   * zoom fails docs/05's readability rule.
   */
  private strokeWidth(screenPx: number): number {
    return clamp(
      screenPx / this.zoomValue,
      this.hexSizeValue * MIN_STROKE_HEX_FRACTION,
      this.hexSizeValue * MAX_STROKE_HEX_FRACTION,
    );
  }

  // -------------------------------------------------------------------------
  // Vector layers
  // -------------------------------------------------------------------------

  /**
   * One stroked path per owner colour, over the visible set. Edges are drawn
   * where two hexes disagree about their owner — including where the neighbour
   * is off the map, which outlines the coastline of the world for free.
   */
  private redrawBorders(): void {
    this.borderGfx.clear();
    if (this.visibleCoords.length === 0) return;

    const segmentsByColor = new Map<number, number[]>();

    for (const coord of this.visibleCoords) {
      const state = this.hexByKey.get(hexKey(coord));
      if (state === undefined || state.owner === null) continue;

      const centre = hexToPixel(coord, this.hexSizeValue);
      const color = this.colorFor(state.owner);

      for (let direction = 0; direction < HEX_DIRECTION_COUNT; direction++) {
        const neighbour = this.hexByKey.get(hexKey(hexNeighbor(coord, direction)));
        const neighbourOwner = neighbour?.owner ?? null;
        if (neighbourOwner === state.owner) continue;

        const first = edgeCornerIndex(direction);
        const a = this.cornerOffsets[first];
        const b = this.cornerOffsets[(first + 1) % HEX_DIRECTION_COUNT];
        if (a === undefined || b === undefined) continue;

        let flat = segmentsByColor.get(color);
        if (flat === undefined) {
          flat = [];
          segmentsByColor.set(color, flat);
        }
        flat.push(centre.x + a.x, centre.y + a.y, centre.x + b.x, centre.y + b.y);
      }
    }

    const width = this.strokeWidth(BORDER_SCREEN_PX);

    // Pass 1: the casing. Every border edge, regardless of owner, stroked dark
    // and wider underneath the coloured line.
    //
    // This is docs/05's core rule made mechanical instead of hoped for. A
    // coloured border is only legible against terrain it happens to contrast
    // with, and some kingdom colours sit very close to some biomes — the yellow
    // against desert, the bluish green against forest. A casing guarantees
    // separation from *any* ground, and from any world-event tint drawn under it.
    for (const flat of segmentsByColor.values()) {
      for (let i = 0; i < flat.length; i += 4) {
        this.borderGfx.moveTo(flat[i] ?? 0, flat[i + 1] ?? 0);
        this.borderGfx.lineTo(flat[i + 2] ?? 0, flat[i + 3] ?? 0);
      }
    }
    this.borderGfx.stroke({
      width: width * BORDER_CASING_SCALE,
      color: BORDER_CASING,
      alpha: 0.55,
      cap: "round",
      join: "round",
    });

    // Pass 2: the kingdom colours on top.
    for (const [color, flat] of segmentsByColor) {
      for (let i = 0; i < flat.length; i += 4) {
        this.borderGfx.moveTo(flat[i] ?? 0, flat[i + 1] ?? 0);
        this.borderGfx.lineTo(flat[i + 2] ?? 0, flat[i + 3] ?? 0);
      }
      // `stroke()` commits and clears the active path, so the next colour starts
      // clean — this is what keeps the whole border layer to a handful of calls.
      this.borderGfx.stroke({ width, color, alpha: 1, cap: "round", join: "round" });
    }
  }

  /**
   * Capture meters. A hex being taken has to be obvious — this is the only
   * feedback a player gets that they are losing ground before they lose it — so
   * it is both a tinted rim on the hex and an explicit bar.
   */
  private redrawCapture(): void {
    this.captureGfx.clear();
    if (this.visibleCoords.length === 0) return;

    const size = this.hexSizeValue;
    const barWidth = size * 1.0;
    const barHeight = Math.max(size * 0.18, 2);
    const barY = size * 0.44;
    const rimWidth = this.strokeWidth(BORDER_SCREEN_PX * 0.8);

    // One phase for the whole frame, so every contested hex pulses together
    // rather than each drifting on its own timer — a frontline under pressure
    // should read as one thing happening.
    const pulse = 0.5 + 0.5 * Math.sin((this.timeMs / 1000) * CONTEST_PULSE_HZ * Math.PI * 2);

    for (const coord of this.visibleCoords) {
      const key = hexKey(coord);
      const state = this.hexByKey.get(key);
      if (state === undefined) continue;

      const centre = hexToPixel(coord, size);

      // Fortified hexes get a cool rim in the same pass, since it is the same
      // per-hex overlay shape and a second pass would cost another clear.
      if (state.fortified) {
        const ring: number[] = [];
        for (const offset of this.cornerOffsets) {
          ring.push(centre.x + offset.x * 0.86, centre.y + offset.y * 0.86);
        }
        this.captureGfx
          .poly(ring)
          .stroke({ width: rimWidth * 1.4, color: FORTIFY_RING, alpha: 0.85 });
      }

      // A flash expands and fades out of the hex — the acknowledgement that
      // something happened here, whether a capture or a refusal.
      const flash = this.flashes.get(key);
      if (flash !== undefined) {
        const t = 1 - flash.remainingMs / FLASH_MS;
        const ring: number[] = [];
        const spread = 1 + t * 0.55;
        for (const offset of this.cornerOffsets) {
          ring.push(centre.x + offset.x * spread, centre.y + offset.y * spread);
        }
        this.captureGfx
          .poly(ring)
          .stroke({ width: rimWidth * (2.4 - t * 1.6), color: flash.color, alpha: (1 - t) * 0.9 });

        const fill: number[] = [];
        for (const offset of this.cornerOffsets) {
          fill.push(centre.x + offset.x, centre.y + offset.y);
        }
        this.captureGfx.poly(fill).fill({ color: flash.color, alpha: (1 - t) * 0.3 });
      }

      const progress = clamp01(this.captureEased.get(key) ?? state.capture);
      if (progress < CAPTURE_VISIBLE_THRESHOLD) continue;

      const rim: number[] = [];
      for (const offset of this.cornerOffsets) {
        rim.push(centre.x + offset.x, centre.y + offset.y);
      }
      // Urgency rises with progress: a hex about to fall pulses hardest.
      const urgency = 0.25 + 0.6 * progress;
      this.captureGfx
        .poly(rim)
        .stroke({ width: rimWidth * (1 + progress * pulse * 0.7), color: CAPTURE_FILL, alpha: urgency });

      const left = centre.x - barWidth / 2;
      const top = centre.y + barY - barHeight / 2;
      this.captureGfx
        .roundRect(left, top, barWidth, barHeight, barHeight / 2)
        .fill({ color: CAPTURE_TRACK, alpha: 0.7 });
      this.captureGfx
        .roundRect(left, top, Math.max(barHeight, barWidth * progress), barHeight, barHeight / 2)
        .fill({ color: CAPTURE_FILL, alpha: 0.95 });
    }
  }

  /**
   * Troop density, drawn as a disc per hex in the owner's colour.
   *
   * Border pressure is what the GDD calls the one core combat verb, and it was
   * previously invisible: the number existed on every hex and appeared only in
   * the inspector, and only for a hex you clicked. A player could not see where
   * their own army was, let alone anyone else's.
   *
   * Radius scales with the square root of the count rather than linearly, so a
   * stack four times larger reads as twice the marker instead of swamping the
   * map — the same reason proportional-symbol maps have used sqrt for a century.
   */
  private redrawTroops(): void {
    this.troopGfx.clear();
    if (this.visibleCoords.length === 0) return;

    const size = this.hexSizeValue;
    const maxRadius = size * 0.34;
    const casing = this.strokeWidth(1.4);

    // Fades out at continent zoom, where individual garrisons stop being
    // readable and ownership mass is the thing that matters instead.
    const hexScreenPx = size * this.zoomValue;
    const zoomAlpha = clamp01((hexScreenPx - 7) / 12);
    if (zoomAlpha <= 0) return;

    for (const coord of this.visibleCoords) {
      const state = this.hexByKey.get(hexKey(coord));
      if (state === undefined || state.owner === null) continue;
      if (state.troops < TROOP_VISIBLE_MIN) continue;

      const strength = clamp01(Math.sqrt(state.troops) / Math.sqrt(TROOP_FULL_SCALE));
      const radius = maxRadius * (0.34 + 0.66 * strength);
      const centre = hexToPixel(coord, size);

      this.troopGfx
        .circle(centre.x, centre.y, radius)
        .fill({ color: this.colorFor(state.owner), alpha: 0.92 * zoomAlpha })
        .stroke({ width: casing, color: TROOP_CASING, alpha: 0.75 * zoomAlpha });
    }
  }


  /**
   * Building glyphs, as a light sprite over a dark one scaled up slightly. The
   * halo is what makes a single-colour glyph legible over six different biome
   * fills without needing per-biome art.
   */
  private redrawBuildings(): void {
    let index = 0;
    const glyphRadius = this.hexSizeValue * 0.42;

    for (const coord of this.visibleCoords) {
      const state = this.hexByKey.get(hexKey(coord));
      if (state === undefined || state.building === null) continue;
      const texture = this.buildingTextures.get(state.building);
      if (texture === undefined) continue;

      const centre = hexToPixel(coord, this.hexSizeValue);

      // The halo stays dark: it is what keeps a single-colour glyph legible over
      // six different biome fills without per-biome art.
      const halo = this.buildingSpriteAt(index, true, texture);
      halo.texture = texture;
      halo.visible = true;
      halo.tint = BUILDING_HALO;
      halo.position.set(centre.x, centre.y);
      halo.scale.set((glyphRadius * 1.3) / TEXTURE_HEX_RADIUS);
      halo.alpha = 0.8;

      // The glyph carries its owner's colour, brightened toward white so it
      // still reads as a structure rather than dissolving into the ownership
      // fill underneath it. One shape, every kingdom — which is the whole point
      // of drawing these in code.
      const glyph = this.buildingSpriteAt(index, false, texture);
      glyph.texture = texture;
      glyph.visible = true;
      glyph.tint =
        state.owner === null
          ? BUILDING_GLYPH
          : mixColors(this.colorFor(state.owner), BUILDING_GLYPH, 0.55);
      glyph.position.set(centre.x, centre.y);
      glyph.scale.set(glyphRadius / TEXTURE_HEX_RADIUS);
      glyph.alpha = 1;

      index++;
    }

    for (let i = index; i < this.haloSprites.length; i++) {
      const halo = this.haloSprites[i];
      const glyph = this.glyphSprites[i];
      if (halo !== undefined) halo.visible = false;
      if (glyph !== undefined) glyph.visible = false;
    }
  }

  private redrawHighlights(): void {
    this.drawHover();
    this.drawSelection();
    this.drawClaimPreview();
  }

  private drawHover(): void {
    this.hoverGfx.clear();
    if (this.hover === null) return;
    this.strokeHex(this.hoverGfx, this.hover, {
      width: this.strokeWidth(HIGHLIGHT_SCREEN_PX),
      color: HOVER_OUTLINE,
      alpha: 0.85,
    });
  }

  private drawSelection(): void {
    this.selectionGfx.clear();
    if (this.selected === null) return;
    // Two concentric strokes: the dark one guarantees contrast against a
    // similarly-coloured territory fill, the accent one is the actual signal.
    this.strokeHex(this.selectionGfx, this.selected, {
      width: this.strokeWidth(HIGHLIGHT_SCREEN_PX * 2.2),
      color: 0x14161c,
      alpha: 0.55,
    });
    this.strokeHex(this.selectionGfx, this.selected, {
      width: this.strokeWidth(HIGHLIGHT_SCREEN_PX * 1.2),
      color: SELECTION_OUTLINE,
      alpha: 1,
    });
  }

  private drawClaimPreview(): void {
    this.previewGfx.clear();
    if (this.claimPreview.length === 0) return;

    const polygons: number[][] = [];
    for (const coord of this.claimPreview) {
      const centre = hexToPixel(coord, this.hexSizeValue);
      const points: number[] = [];
      for (const offset of this.cornerOffsets) {
        points.push(centre.x + offset.x, centre.y + offset.y);
      }
      polygons.push(points);
      this.previewGfx.poly(points).fill({ color: CLAIM_PREVIEW, alpha: 0.16 });
    }

    // Outlines are batched into a single stroked path so a preview covering the
    // whole frontier is still one stroke, not one per hex.
    for (const points of polygons) this.previewGfx.poly(points);
    this.previewGfx.stroke({
      width: this.strokeWidth(HIGHLIGHT_SCREEN_PX * 0.8),
      color: CLAIM_PREVIEW,
      alpha: 0.6,
    });
  }

  private strokeHex(
    target: Graphics,
    coord: HexCoord,
    style: { width: number; color: number; alpha: number },
  ): void {
    const centre = hexToPixel(coord, this.hexSizeValue);
    const points: number[] = [];
    for (const offset of this.cornerOffsets) {
      points.push(centre.x + offset.x, centre.y + offset.y);
    }
    target.poly(points).stroke({
      width: style.width,
      color: style.color,
      alpha: style.alpha,
      join: "round",
    });
  }
}

function sameCoord(a: HexCoord | null, b: HexCoord | null): boolean {
  if (a === null || b === null) return a === b;
  return a.q === b.q && a.r === b.r;
}

function sameCoords(a: readonly HexCoord[], b: readonly HexCoord[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (left === undefined || right === undefined) return false;
    if (left.q !== right.q || left.r !== right.r) return false;
  }
  return true;
}

/** A multiplier expressed as a grey, for baking shading into a tinted texture. */
function greyOf(multiplier: number): number {
  const v = Math.max(0, Math.min(255, Math.round(255 * multiplier)));
  return (v << 16) | (v << 8) | v;
}


/**
 * A stable hash of a hex coordinate.
 *
 * Decoration placement must be a pure function of position: a tree that moved
 * when you panned away and back would be more distracting than no tree at all.
 */
function hashCoord(coord: HexCoord): number {
  let h = Math.imul(coord.q | 0, 0x27d4eb2d) ^ Math.imul(coord.r | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  return (h ^ (h >>> 12)) >>> 0;
}

/** Uniform value in [0,1) from an integer seed. */
function rand(seed: number): number {
  let t = (seed + 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** Blend two packed RGB colours, `t` = 0 keeps `a`, `t` = 1 keeps `b`. */
function mixColors(a: number, b: number, t: number): number {
  const k = Math.max(0, Math.min(1, t));
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return (r << 16) | (g << 8) | bl;
}
